class ScrollImageSwap {
  constructor() {
    this.productCards = [];
    this.lastScrollY = window.scrollY;
    this.ticking = false;
    this.scrollThreshold = 50; 
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.collectProductCards();
    this.bindEvents();
    this.createIntersectionObserver();
  }

  collectProductCards() {
    const cards = document.querySelectorAll('.product-card__media--hoverable');
    
    cards.forEach((card, index) => {
      const images = card.querySelectorAll('img');
      if (images.length >= 2) {
        this.productCards.push({
          element: card,
          images: Array.from(images),
          currentIndex: 0,
          isVisible: false,
          lastSwapScroll: window.scrollY
        });
      }
    });
  }

  bindEvents() {
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    
    
    document.addEventListener('mousemove', (e) => this.onMouseMove(e), { passive: true });
    
    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', (e) => this.onDeviceOrientation(e), { passive: true });
    }
  }

  createIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const cardData = this.productCards.find(c => c.element === entry.target);
        if (cardData) {
          cardData.isVisible = entry.isIntersecting;
          
          // Trigger swap when entering viewport
          if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
            this.swapImage(cardData, true);
          }
        }
      });
    }, options);

    this.productCards.forEach(card => {
      this.observer.observe(card.element);
    });
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.handleScroll();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollDelta = Math.abs(currentScrollY - this.lastScrollY);
    
    this.productCards.forEach(cardData => {
      if (cardData.isVisible) {
        const scrollSinceLastSwap = Math.abs(currentScrollY - cardData.lastSwapScroll);
        
        // Swap image if scrolled enough since last swap
        if (scrollSinceLastSwap > this.scrollThreshold) {
          this.swapImage(cardData);
          cardData.lastSwapScroll = currentScrollY;
        }
      }
    });
    
    this.lastScrollY = currentScrollY;
  }

  onMouseMove(e) {
    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const mouseY = e.clientY;
    
    // Only trigger if mouse is in certain zones
    const zone = mouseY / viewportHeight;
    
    this.productCards.forEach(cardData => {
      if (cardData.isVisible) {
        const rect = cardData.element.getBoundingClientRect();
        const cardCenterY = rect.top + rect.height / 2;
        const distanceFromMouse = Math.abs(e.clientY - cardCenterY);
        
        // If mouse is close to the card, consider swapping
        if (distanceFromMouse < 100 && Math.random() > 0.95) {
          this.swapImage(cardData);
        }
      }
    });
  }

  onDeviceOrientation(e) {
    // Use device tilt to trigger image swaps on mobile
    const tiltThreshold = 15;
    
    if (Math.abs(e.beta) > tiltThreshold || Math.abs(e.gamma) > tiltThreshold) {
      this.productCards.forEach(cardData => {
        if (cardData.isVisible && Math.random() > 0.9) {
          this.swapImage(cardData);
        }
      });
    }
  }

  swapImage(cardData, animate = true) {
    const { element, images, currentIndex } = cardData;
    
    if (images.length < 2) return;
    
    // Calculate next index
    const nextIndex = (currentIndex + 1) % images.length;
    
    // Apply transition
    if (animate) {
      element.style.transition = 'opacity 0.3s ease';
    }
    
    // Hide current image, show next
    images.forEach((img, idx) => {
      if (idx === nextIndex) {
        img.style.opacity = '1';
        img.style.zIndex = '1';
        img.classList.remove('hidden', 'hover-img');
      } else {
        img.style.opacity = '0';
        img.style.zIndex = '0';
      }
    });
    
    // Update current index
    cardData.currentIndex = nextIndex;
  }
}

class ParallaxImageEffect {
  constructor() {
    this.cards = [];
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const mediaElements = document.querySelectorAll('.product-card__media--hoverable');
    
    mediaElements.forEach(media => {
      const images = media.querySelectorAll('img');
      if (images.length >= 2) {
        this.cards.push({
          element: media,
          images: Array.from(images)
        });
        
        // Set initial styles for parallax effect
        images.forEach((img, idx) => {
          img.style.transition = 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
          if (idx === 0) {
            img.style.opacity = '1';
          } else {
            img.style.opacity = '0';
          }
        });
      }
    });

    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  onScroll() {
    this.cards.forEach(card => {
      const rect = card.element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate how far through the viewport the card is (0 to 1)
      const progress = 1 - (rect.top / viewportHeight);
      
      if (progress > 0 && progress < 1.5) {
        // Determine which image to show based on scroll progress
        const imageIndex = Math.floor(progress * card.images.length) % card.images.length;
        
        card.images.forEach((img, idx) => {
          img.style.opacity = idx === imageIndex ? '1' : '0';
          img.style.zIndex = idx === imageIndex ? '1' : '0';
        });
      }
    });
  }
}



// const scrollImageSwap = new ScrollImageSwap();

// Option 2: Parallax effect (uncomment to use instead)
const parallaxEffect = new ParallaxImageEffect();